/**
 * EmailTransmissionForm
 *
 * Form for email transmission: recipient + email + subject + body + cc.
 * Works with EmailTransmission type from the discriminated union.
 */

import React, { useCallback, useMemo } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { FormField } from './FormField';
import {
  inputErrorStyles,
  inputStyles,
  inputWarningStyles,
  textareaStyles,
} from './styles';
import type { EmailTransmissionFormProps } from './types';

const SUBJECT_LIMIT = 100;
const BODY_LIMIT = 5000;
const WARNING_THRESHOLD = 0.8;

export const EmailTransmissionForm: React.FC<EmailTransmissionFormProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  errors,
  matterCmsEmail,
}) => {
  const ccValue = value.cc?.join(', ') || '';

  const handleCcChange = (inputValue: string) => {
    if (!inputValue.trim()) {
      onChange({ ...value, cc: undefined });
      return;
    }
    const emails = inputValue
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
    onChange({ ...value, cc: emails.length > 0 ? emails : undefined });
  };

  // Check if the matter CMS email is currently in the CC list
  const isCmsEmailInCc = useMemo(() => {
    if (!matterCmsEmail || !value.cc) return false;
    return value.cc.some(
      (email) => email.toLowerCase() === matterCmsEmail.toLowerCase(),
    );
  }, [matterCmsEmail, value.cc]);

  // Handler to toggle CMS email in CC list
  const handleToggleCmsEmailCc = useCallback(
    (checked: boolean) => {
      if (!matterCmsEmail) return;

      const currentCc = value.cc ?? [];
      let newCc: string[];

      if (checked) {
        // Add CMS email if not already present
        if (
          !currentCc.some(
            (e) => e.toLowerCase() === matterCmsEmail.toLowerCase(),
          )
        ) {
          newCc = [...currentCc, matterCmsEmail];
        } else {
          newCc = currentCc;
        }
      } else {
        // Remove CMS email
        newCc = currentCc.filter(
          (e) => e.toLowerCase() !== matterCmsEmail.toLowerCase(),
        );
      }

      onChange({ ...value, cc: newCc.length > 0 ? newCc : undefined });
    },
    [matterCmsEmail, value, onChange],
  );

  const subjectCount = value.subject?.length || 0;
  const bodyCount = value.body?.length || 0;
  const subjectWarning =
    subjectCount >= SUBJECT_LIMIT * WARNING_THRESHOLD &&
    subjectCount < SUBJECT_LIMIT;
  const bodyWarning =
    bodyCount >= BODY_LIMIT * WARNING_THRESHOLD && bodyCount < BODY_LIMIT;

  return (
    <div className={cn('space-y-2.5', className)}>
      <FormField
        label="Recipient"
        htmlFor="email-displayName"
        required
        error={errors?.display_name}
      >
        <Input
          id="email-displayName"
          type="text"
          placeholder="Blue Cross Blue Shield"
          value={value.display_name || ''}
          onChange={(e) => onChange({ ...value, display_name: e.target.value })}
          disabled={disabled}
          className={cn(inputStyles, errors?.display_name && inputErrorStyles)}
        />
      </FormField>

      <FormField
        label="Email"
        htmlFor="email-address"
        required
        error={errors?.email}
      >
        <Input
          id="email-address"
          type="email"
          placeholder="claims@carrier.com"
          value={value.email || ''}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
          disabled={disabled}
          className={cn(inputStyles, errors?.email && inputErrorStyles)}
        />
      </FormField>

      <FormField
        label="CC"
        htmlFor="email-cc"
        error={errors?.cc}
        helperText={!errors?.cc ? 'Comma-separated addresses' : undefined}
      >
        <Input
          id="email-cc"
          type="text"
          placeholder="cc@example.com"
          value={ccValue}
          onChange={(e) => handleCcChange(e.target.value)}
          disabled={disabled}
          className={cn(inputStyles, errors?.cc && inputErrorStyles)}
        />
      </FormField>

      {/* CC Firm CMS Email checkbox */}
      {matterCmsEmail && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="cc-cms-email-manual"
            checked={isCmsEmailInCc}
            onCheckedChange={handleToggleCmsEmailCc}
            disabled={disabled}
          />
          <Label
            htmlFor="cc-cms-email-manual"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            CC firm CMS email ({matterCmsEmail})
          </Label>
        </div>
      )}

      <FormField
        label="Subject"
        htmlFor="email-subject"
        error={errors?.subject}
        characterCount={{
          current: subjectCount,
          max: SUBJECT_LIMIT,
          warningThreshold: WARNING_THRESHOLD,
        }}
        inlineCount
      >
        <Input
          id="email-subject"
          type="text"
          placeholder="Lien Notice - [Matter Name]"
          value={value.subject || ''}
          onChange={(e) =>
            onChange({ ...value, subject: e.target.value || undefined })
          }
          disabled={disabled}
          maxLength={SUBJECT_LIMIT}
          className={cn(
            inputStyles,
            errors?.subject && inputErrorStyles,
            subjectWarning && inputWarningStyles,
          )}
        />
      </FormField>

      <FormField
        label="Message"
        htmlFor="email-body"
        error={errors?.body}
        characterCount={{
          current: bodyCount,
          max: BODY_LIMIT,
          warningThreshold: WARNING_THRESHOLD,
        }}
        inlineCount
      >
        <Textarea
          id="email-body"
          placeholder="Email body content..."
          value={value.body || ''}
          onChange={(e) =>
            onChange({ ...value, body: e.target.value || undefined })
          }
          disabled={disabled}
          maxLength={BODY_LIMIT}
          className={cn(
            textareaStyles,
            errors?.body && inputErrorStyles,
            bodyWarning && inputWarningStyles,
          )}
          rows={3}
        />
      </FormField>
    </div>
  );
};
